import React from "react";
import { Send, Loader2, CheckCircle2, Phone, Mail, MapPin } from "lucide-react";
import { Eyebrow } from "../components/SectionHeading";
import SEO from "../components/SEO";
import { useContactForm, PROJECT_TYPES } from "../hooks/useContactForm";
import { useSiteSettings } from "../hooks/useSiteContent";

const Contact = () => {
  const { form, submitting, submitted, updateField, onSubmit, reset } = useContactForm();
  const update = updateField;
  const settings = useSiteSettings() || {};
  const company = settings.company_name || "CM Lindley LLC";
  const phone = settings.phone || "903-521-2788";
  const email = settings.email || "info@cmlindleyllc.com";
  const addr1 = settings.address_line1 || "476 Highway A1A, Suite 8B";
  const addr2 = settings.address_line2 || "Satellite Beach, FL 32937";
  const mailing = settings.mailing_address || "PO Box 372913 Satellite Beach, FL 32937";
  const phoneHref = `tel:${phone.replace(/[^0-9+]/g, "")}`;

  return (
    <div data-testid="contact-page">
      <SEO
        title="Request a Quote - CM Lindley"
        path="/contact"
        description="Request a quote from CM Lindley. Send lumber takeoffs, steel packages, or full material lists. We respond quickly with clear pricing and timing."
      />
      <section className="bg-[#0A192F] py-20 sm:py-24 lg:py-32" data-testid="contact-header">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <Eyebrow light>Contact</Eyebrow>
          <h1 className="mt-6 font-display font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
            Let's talk <span className="text-[#D4AF37]">materials.</span>
          </h1>
          <p className="mt-6 sm:mt-8 text-white/75 max-w-2xl text-base sm:text-lg">
            Give us a call at{" "}
            <a
              href="tel:+19035212788"
              data-testid="contact-hero-phone"
              className="text-[#D4AF37] font-semibold whitespace-nowrap hover:underline"
            >
              903-521-2788
            </a>
            , tell us about your project - send plans, takeoffs, or a quick description and CM Lindley
            will reply with a quote and clear next steps.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-10">
          {/* Business info block - above the form */}
          <div data-testid="contact-business-info" className="mb-10 sm:mb-14 bg-[#F0F4F8] border-l-4 border-[#D4AF37] p-7 sm:p-9">
            <Eyebrow>Reach Us Directly</Eyebrow>
            <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl text-[#0A192F] tracking-tighter" data-testid="contact-business-name">
              {company}
            </h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-6 sm:gap-8">
              <div className="space-y-4">
                <InfoRow icon={Phone} label="Phone">
                  <a href={phoneHref} data-testid="contact-info-phone" className="text-[#0A192F] hover:text-[#D4AF37] font-semibold text-base">
                    {phone}
                  </a>
                </InfoRow>
                <InfoRow icon={Mail} label="Email">
                  <a href={`mailto:${email}`} data-testid="contact-info-email" className="text-[#0A192F] hover:text-[#D4AF37] font-semibold text-base break-all">
                    {email}
                  </a>
                </InfoRow>
              </div>
              <div className="space-y-4">
                <InfoRow icon={MapPin} label="Office">
                  <div data-testid="contact-info-address" className="text-[#0A192F] text-sm leading-snug">
                    {addr1}<br />
                    {addr2}
                  </div>
                </InfoRow>
                <InfoRow icon={MapPin} label="Mailing">
                  <div data-testid="contact-info-mailing" className="text-[#0A192F] text-sm leading-snug">
                    {mailing}
                  </div>
                </InfoRow>
              </div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="border border-gray-200 p-7 sm:p-10 lg:p-12 bg-white"
          >
            {submitted ? (
              <div data-testid="contact-success" className="text-center py-12">
                <CheckCircle2 size={48} className="text-[#D4AF37] mx-auto" />
                <h3 className="mt-6 font-display font-black text-2xl text-[#0A192F]">
                  Quote request received.
                </h3>
                <p className="mt-3 text-[#0A192F]/70 max-w-md mx-auto">
                  Thank you for contacting CM Lindley. We have received your information and someone will be in touch directly.
                </p>
                <button
                  type="button"
                  data-testid="contact-success-reset"
                  onClick={reset}
                  className="mt-8 inline-flex items-center gap-2 font-display text-xs uppercase tracking-[0.3em] text-[#0A192F] hover:text-[#D4AF37]"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <>
                <Eyebrow>Request a Quote</Eyebrow>
                <h2 className="mt-5 font-display font-black text-2xl sm:text-3xl text-[#0A192F] tracking-tighter">
                  Tell us about your project
                </h2>
                <p className="mt-3 text-sm text-[#0A192F]/65">
                  Fill out the form below - fields marked with * are required.
                </p>

                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Name *"
                    testId="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    required
                  />
                  <Field
                    label="Company"
                    testId="contact-input-company"
                    value={form.company}
                    onChange={update("company")}
                  />
                  <Field
                    label="Email *"
                    type="email"
                    testId="contact-input-email"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                  <Field
                    label="Phone"
                    type="tel"
                    testId="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                  />

                  <div>
                    <Label>Project Type</Label>
                    <select
                      data-testid="contact-input-project-type"
                      value={form.project_type}
                      onChange={update("project_type")}
                      className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none transition-colors"
                    >
                      {PROJECT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Field
                    label="Project Date"
                    type="date"
                    testId="contact-input-project-date"
                    value={form.project_date}
                    onChange={update("project_date")}
                  />

                  <div className="sm:col-span-2">
                    <Field
                      label="Location of Project"
                      testId="contact-input-project-location"
                      placeholder="City, State or job-site address"
                      value={form.project_location}
                      onChange={update("project_location")}
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label>Message *</Label>
                    <textarea
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      rows={6}
                      required
                      placeholder="Tell us about your project, timeline, or specific materials you need. If you have construction plans or a cut list, mention it here - we'll request the attachments in our reply."
                      className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none transition-colors resize-y"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#0A192F] text-white px-10 py-5 font-display text-xs uppercase tracking-[0.3em] font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Request a Quote <Send size={16} />
                    </>
                  )}
                </button>

                <p className="mt-4 text-xs text-[#0A192F]/50">
                  By submitting, you agree to be contacted by CM Lindley regarding your inquiry.
                </p>
              </>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 bg-[#0A192F] flex items-center justify-center flex-shrink-0">
      <Icon size={16} className="text-[#D4AF37]" />
    </div>
    <div className="min-w-0">
      <div className="font-display text-[10px] uppercase tracking-[0.3em] text-[#0A192F]/60 font-semibold">
        {label}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  </div>
);

const Label = ({ children }) => (
  <label className="font-display text-[10px] uppercase tracking-[0.3em] text-[#0A192F]/70 font-semibold">
    {children}
  </label>
);

const Field = ({ label, testId, ...props }) => (
  <div>
    <Label>{label}</Label>
    <input
      {...props}
      data-testid={testId}
      className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none transition-colors"
    />
  </div>
);

export default Contact;
